import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../../components/Header';
import Loader from '../../../components/Loader/Loader';
import Activities from '../../../components/Activities/Activity';
import AddHotWorkApprovalModal from './NewHVModal';
import {
  getHotWorkApprovalById,
  deleteHotWorkApproval,
  submitAssessment,
  notifyApproval,
  notifyFinalClearance,
  getAppointedPersons,
  markApproved,
  rejectApproval,
  responsiblePersonClearance,
  responsiblePersonCancel,
  appointedPersonClearance,
  appointedPersonCancel,
  getHotWorkApprovalDownload,
} from '../../../services/hotWorkApprovalService';
import { AlertCircle, X, Pencil, Trash2, CheckCircle, Download } from 'lucide-react';
import { useProfile } from '../../../Contexts/ProfileContext';
import ConfirmationModal from '../../../components/RejectButton/ConfirmationModal';

// ASSESSMENT CHECKLIST (unchanged)
const ASSESSMENT_SECTIONS: {
  title: string;
  items: { id: number; key: string; label: string }[];
}[] = [
  {
    title: 'Required Precautions',
    items: [
      { id: 1, key: 'sprinklers_operable', label: 'Available sprinklers, hose streams and extinguishers are in service/ operable' },
      { id: 2, key: 'hot_work_equipment_good_repair', label: 'Hot/Work equipment in good repair' },
    ],
  },
  {
    title: 'Requirements within 3.5ft (11 M) Of Work',
    items: [
      { id: 3, key: 'flammable_removed', label: 'Flammable liquids, dust, lint and oily deposits removed' },
      { id: 4, key: 'explosive_atmosphere_eliminated', label: 'Explosive atmosphere area eliminated' },
      { id: 5, key: 'floor_swept_clean', label: 'Floor swept clean' },
      {
        id: 6,
        key: 'combustible_floor_wetdown',
        label: 'Combustible floor wet down, covered with damp sand or with fire resistive sheets',
      },
      {
        id: 7,
        key: 'combustible_protected',
        label: 'Remove other combustible were possible. Otherwise protect with fireresistive tarpaulins or metal shields',
      },
      { id: 8, key: 'wall_floor_openings_covered', label: 'All wall and floor openings covered' },
      { id: 9, key: 'tarpaulins_beneath_work', label: 'Fire-resistive tarpaulins suspended beneath work' },
      {
        id: 10,
        key: 'ducts_conveyors_protected',
        label: 'Protect or shutdown ducts and conveyors that might carry sparks to distant combustibles',
      },
    ],
  },
  {
    title: 'Work on Walls, Ceilings or Roofs',
    items: [
      {
        id: 11,
        key: 'noncombustible_construction',
        label: 'Construction is noncombustible and without combustible covering or insulation',
      },
      { id: 12, key: 'combustible_moved_away', label: 'Combustible on either side of the walls, ceiling or roofs are moved away' },
    ],
  },
  {
    title: 'Work on Enclosed Equipment',
    items: [
      { id: 13, key: 'enclosed_equipment_cleaned', label: 'Enclosed equipment cleaned of all combustibles' },
      { id: 14, key: 'container_purged', label: 'Container purged of all flammable liquids/vipours' },
      {
        id: 15,
        key: 'pressurized_vessels_removed',
        label: 'Pressurized vessels, Piping and equipment removed from service, isolated and vented',
      },
    ],
  },
  {
    title: 'Hot Work Monitor/ Hot Work Area Monitoring',
    items: [
      {
        id: 16,
        key: 'monitor_provided_60min',
        label: 'Hot Work Monitor will be provided during and for 60 minutes after work, including any coffee or lunch breaks',
      },
      {
        id: 17,
        key: 'monitor_supplied_extinguishers',
        label: 'Hot Work Monitor supplied with suitable extinguishers, and where practical, a charged small hose',
      },
      { id: 18, key: 'monitor_trained', label: 'Hot Work Monitor is trained in use of equipment and in sounding alarm' },
      { id: 19, key: 'monitor_adjoining_areas', label: 'Hot Work Monitor may be required in adjoining areas, above and below' },
      { id: 20, key: 'monitor_4hours_after', label: 'Monitor Hot Work area for 4 hours after job is completed' },
    ],
  },
];

const ALL_ITEMS = ASSESSMENT_SECTIONS.flatMap((s) => s.items);

// HELPERS (unchanged)
const buildInitialChecked = (): Record<string, boolean> => {
  const map: Record<string, boolean> = {};
  ALL_ITEMS.forEach(({ key }) => {
    map[key] = false;
  });
  return map;
};

const hydrateChecked = (assessment_answers: any): Record<string, boolean> => {
  const base = buildInitialChecked();
  if (!assessment_answers) return base;

  Object.keys(base).forEach((key) => {
    const val = assessment_answers[key];
    if (val === undefined || val === null) return;
    base[key] = typeof val === 'object' ? !!val.checked : !!val;
  });
  return base;
};

const buildAssessmentPayload = (checkedItems: Record<string, boolean>, otherPrecautions: string) => {
  const assessment_answers: Record<string, { question_id: number; question: string; checked: boolean }> = {};
  ALL_ITEMS.forEach(({ id, key, label }) => {
    assessment_answers[key] = {
      question_id: id,
      question: label,
      checked: !!checkedItems[key],
    };
  });
  return { assessment_answers, other_precautions_taken: otherPrecautions };
};

// COMPONENT
const HotWorkApprovalDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [item, setItem] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [showError, setShowError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Assessment
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(buildInitialChecked());
  const [otherPrecautions, setOtherPrecautions] = useState('');
  const [verifyChecked, setVerifyChecked] = useState(false);
  const [assessmentDone, setAssessmentDone] = useState(false);
  const [assessmentSubmitting, setAssessmentSubmitting] = useState(false);

  // View Details
  const [showViewDetailsModal, setShowViewDetailsModal] = useState(false);

  // Notify for Approval
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifySubmitting, setNotifySubmitting] = useState(false);
  const [approvalNotified, setApprovalNotified] = useState(false);

  const [showNotifyClearanceModal, setShowNotifyClearanceModal] = useState(false);
  const [notifyClearanceSubmitting, setNotifyClearanceSubmitting] = useState(false);
  const [clearanceNotified, setClearanceNotified] = useState(false);
  const [selectedClearanceAppointedId, setSelectedClearanceAppointedId] = useState<number | null>(null);

  const [showClearanceNotification, setShowClearanceNotification] = useState(false);

  const [appointedPersons, setAppointedPersons] = useState<any[]>([]);
  const [selectedAppointedId, setSelectedAppointedId] = useState<number | null>(null);
  const [showNotifyNotification, setShowNotifyNotification] = useState(false);

  // Approve
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [approveIds, setApproveIds] = useState<string[]>([]);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const [clearanceApproveIds, setClearanceApproveIds] = useState<string[]>([]);

  const [assessmentCompleted, setAssessmentCompleted] = useState(false);
  const [approvalPending, setApprovalPending] = useState(false);
  const [approved, setApproved] = useState(false);

  const [showClearanceModal, setShowClearanceModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const [clearanceSubmitting, setClearanceSubmitting] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const [clearanceDone, setClearanceDone] = useState(false);
  const [showNotifyClearanceButton, setShowNotifyClearanceButton] = useState(false);

  const [clearanceDoneAp, setclearanceDoneAp] = useState(false);

  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [downloadSubmitting, setDownloadSubmitting] = useState(false);

  const [hotWorkPerformerName, setHotWorkPerformerName] = useState('');
  const [fireWatchMonitorName, setFireWatchMonitorName] = useState('');

  const profile = useProfile();
  const isTechnician = profile?.role === 'technician';
  const [currentUserId, setCurrentUserId] = useState('');

  const [remainingTime, setRemainingTime] = useState('');
  const [isClearanceEnabled, setIsClearanceEnabled] = useState(false);

  // ── FETCH ──
  const fetchDetails = async () => {
    try {
      const res = await getHotWorkApprovalById(id!);

      const hotWork = res?.data?.data || res?.data || res;

      setItem(hotWork);

      setHotWorkPerformerName(hotWork?.hot_work_performer?.name || '');

      setFireWatchMonitorName(hotWork?.fire_watch_monitor?.name || '');

      const statusCode = hotWork?.['status-details']?.code || hotWork?.status_code || '';

      setAssessmentDone(false);
      setAssessmentCompleted(false);
      setApprovalPending(false);
      setApprovalNotified(false);
      setApproved(false);

      if (statusCode === 'ASSESSMENT_COMPLETED') {
        setAssessmentDone(true);
        setAssessmentCompleted(true);
      }

      if (statusCode === 'APPROVAL_PENDING') {
        setAssessmentDone(true);
        setAssessmentCompleted(true);
        setApprovalPending(true);
        setApprovalNotified(true);
      }

      if (statusCode === 'APPROVED') {
        setAssessmentDone(true);
        setAssessmentCompleted(true);
        setApprovalPending(true);
        setApprovalNotified(true);
        setApproved(true);
      }

      if (statusCode === 'RP_CLEARANCE_PENDING') {
        setAssessmentDone(true);
        setAssessmentCompleted(true);
        setApprovalPending(true);
        setApprovalNotified(true);
        setApproved(true);
      }

      if (statusCode === 'RP_CLEARED') {
        setAssessmentDone(true);
        setAssessmentCompleted(true);
        setApprovalPending(true);
        setApprovalNotified(true);
        setApproved(true);
        setClearanceDone(true);
        setShowNotifyClearanceButton(true);
      }

      if (statusCode === 'AP_CLEARANCE_PENDING') {
        setAssessmentDone(true);
        setAssessmentCompleted(true);
        setApprovalPending(true);
        setApprovalNotified(true);
        setApproved(true);
        setClearanceDone(true);
      }

      if (statusCode === 'AP_CLEARED') {
        setAssessmentDone(true);
        setAssessmentCompleted(true);
        setApprovalPending(true);
        setApprovalNotified(true);
        setApproved(true);
        setClearanceDone(true);
        setclearanceDoneAp(true);
        setShowDownloadButton(true);
      }

      const getPendingApproverIds = (workflowStages: any[], stageName: string) =>
        (Array.isArray(workflowStages) ? workflowStages : [])
          .filter((stage: any) => stage?.stage?.toUpperCase() === stageName.toUpperCase())
          .flatMap((stage: any) =>
            (Array.isArray(stage?.approvers) ? stage.approvers : [])
              .filter((approver: any) => approver?.status === 'PENDING')
              .map((approver: any) => (approver?.user?.id ?? approver?.user_id ?? approver?.id)?.toString())
              .filter(Boolean),
          );

      // APPROVAL USERS
      const pendingApprovalIds = getPendingApproverIds(hotWork.workflow_stages, 'APPROVAL');

      setApproveIds(pendingApprovalIds);

      const pendingClearanceIds = getPendingApproverIds(hotWork.workflow_stages, 'FINAL_CLEARANCE');

      setClearanceApproveIds(pendingClearanceIds);

      // ASSESSMENT
      const assessment = hotWork?.assessment;

      if (assessment?.assessment_answers) {
        let answers = assessment.assessment_answers;

        if (typeof answers === 'string') {
          answers = JSON.parse(answers);
        }

        setCheckedItems(hydrateChecked(answers));
      }

      if (assessment?.other_precautions_taken) {
        setOtherPrecautions(assessment.other_precautions_taken);
      }

      const finalApprovalStages = Array.isArray(hotWork?.workflow_stages)
        ? hotWork.workflow_stages.filter((stage: any) => stage?.stage?.toUpperCase() === 'APPROVAL')
        : [];

      const latestFinalApprovalStage = finalApprovalStages.length > 0 ? finalApprovalStages[finalApprovalStages.length - 1] : null;

      const approvalStageExists = latestFinalApprovalStage?.status === 'PENDING';

      const finalClearanceStages = Array.isArray(hotWork?.workflow_stages)
        ? hotWork.workflow_stages.filter((stage: any) => stage?.stage?.toUpperCase() === 'FINAL_CLEARANCE')
        : [];

      const latestFinalClearanceStage = finalClearanceStages.length > 0 ? finalClearanceStages[finalClearanceStages.length - 1] : null;

      const finalRejected = latestFinalClearanceStage?.status === 'REJECTED';

      if (approvalStageExists) {
        setApprovalNotified(true);
      } else {
        if (finalRejected) {
          if (latestFinalApprovalStage.status === 'COMPLETED' && latestFinalApprovalStage.id < latestFinalClearanceStage.id)
            setApprovalNotified(false);
        }
      }
    } catch (err) {
      console.error('Failed to fetch Hot Work details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!item?.created_at) return;

    const calculateRemainingTime = () => {
      const createdAt = new Date(item.created_at.replace(' ', 'T') + 'Z');

      const clearanceTime = new Date(createdAt.getTime() + 4 * 60 * 60 * 1000);

      const diff = clearanceTime.getTime() - Date.now();

      if (diff <= 0) {
        setRemainingTime('00:00:00');
        setIsClearanceEnabled(true);
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setRemainingTime(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);

      setIsClearanceEnabled(false);
    };

    calculateRemainingTime();

    const interval = setInterval(calculateRemainingTime, 1000);

    return () => clearInterval(interval);
  }, [item?.created_at]);

  useEffect(() => {
    if (id) fetchDetails();
  }, [id]);

  useEffect(() => {
    setCurrentUserId(localStorage.getItem('currentUserID') || '');
  }, []);

  // ── STATUS COLORS ──
  const getStatusColors = (status: string) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('pending')) return { dot: 'bg-orange-500', badge: 'text-orange-500 bg-orange-50' };
    if (s.includes('completed') || s.includes('approved')) return { dot: 'bg-green-600', badge: 'text-green-600 bg-green-50' };
    return { dot: 'bg-gray-400', badge: 'text-gray-600 bg-gray-50' };
  };

  // ── ASSESSMENT HANDLERS ──
  const toggleCheckItem = (key: string) => setCheckedItems((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleAssessmentNext = async () => {
    if (!verifyChecked) return;

    setAssessmentSubmitting(true);
    try {
      const payload = buildAssessmentPayload(checkedItems, otherPrecautions);
      await submitAssessment(id!, payload);

      setItem((prev: any) => ({
        ...prev,
        status: 'Assessment Completed',
        'status-details': {
          ...(prev?.['status-details'] || {}),
          code: 'ASSESSMENT_COMPLETED',
        },
      }));

      setAssessmentDone(true);
      setAssessmentCompleted(true);
      setShowAssessmentModal(false);
      setVerifyChecked(false);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to submit assessment.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setAssessmentSubmitting(false);
    }
  };

  // ── NOTIFY APPROVAL ──
  const handleNotifyApprovalClick = async () => {
    setShowNotifyModal(true);

    try {
      const res = await getAppointedPersons();
      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name,
      }));
      setAppointedPersons(users);
    } catch (error) {
      console.error('Failed to fetch appointed persons:', error);
    }
  };

  const handleNotifyClearanceClick = async () => {
    setShowNotifyClearanceModal(true);

    try {
      const res = await getAppointedPersons();

      const users = (res?.data ?? res ?? []).map((u: any) => ({
        id: u.id,
        name: u.name || u.full_name,
      }));

      setAppointedPersons(users);
    } catch (error) {
      console.error('Failed to fetch appointed persons:', error);
    }
  };

  const handleNotifyApproval = async () => {
    try {
      if (!selectedAppointedId) {
        alert('Please select an Appointed Person');
        return;
      }

      setNotifySubmitting(true);

      await notifyApproval(id!, {
        user_ids: [selectedAppointedId],
      });

      setItem((prev: any) => ({
        ...prev,
        status: 'Approval Pending',
        'status-details': {
          ...(prev?.['status-details'] || {}),
          code: 'APPROVAL_PENDING',
        },
      }));

      setApprovalNotified(true);

      setShowNotifyModal(false);
      setSelectedAppointedId(null);

      setShowNotifyNotification(true);
      setTimeout(() => setShowNotifyNotification(false), 3000);

      await fetchDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setNotifySubmitting(false);
    }
  };

  const handleNotifyClearance = async () => {
    try {
      if (!selectedClearanceAppointedId) {
        alert('Please select an Appointed Person');
        return;
      }

      setNotifyClearanceSubmitting(true);

      await notifyFinalClearance(id!, {
        user_ids: [selectedClearanceAppointedId],
      });

      setClearanceNotified(true);

      setShowNotifyClearanceModal(false);

      setSelectedClearanceAppointedId(null);

      setShowClearanceNotification(true);

      setTimeout(() => {
        setShowClearanceNotification(false);
      }, 3000);

      await fetchDetails();
    } catch (error) {
      console.error(error);
    } finally {
      setNotifyClearanceSubmitting(false);
    }
  };

  // ── APPROVE ──
  const handleApprove = async () => {
    try {
      setApproveSubmitting(true);

      await markApproved(id!);

      setItem((prev: any) => ({
        ...prev,
        status: 'Approved',
        'status-details': {
          ...(prev?.['status-details'] || {}),
          code: 'APPROVED',
        },
      }));

      setApproved(true);

      setApproved(true);
      setShowApproveModal(false);

      setSuccessMessage('Hot work approval approved successfully.');
      setShowSuccess(true);

      await fetchDetails();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to approve hot work approval.';

      setApiError(message);
      setShowError(true);

      setTimeout(() => setShowError(false), 5000);
    } finally {
      setApproveSubmitting(false);
    }
  };

  const handleReject = async (remarks: string) => {
    try {
      setRejectSubmitting(true);

      await rejectApproval(id!, remarks);

      setShowRejectModal(false);

      setSuccessMessage('Hot work approval rejected successfully.');
      setShowSuccess(true);

      await fetchDetails();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to reject hot work approval.';

      setApiError(message);
      setShowError(true);

      setTimeout(() => setShowError(false), 5000);
    } finally {
      setRejectSubmitting(false);
    }
  };

  const handleAppointedPersonClearance = async () => {
    try {
      setClearanceSubmitting(true);

      await appointedPersonClearance(id!);

      setItem((prev: any) => ({
        ...prev,
        status: 'Final Cleared',
        'status-details': {
          ...(prev?.['status-details'] || {}),
          code: 'FINAL_CLEARED',
        },
      }));

      setShowDownloadButton(true);

      setShowClearanceModal(false);

      setSuccessMessage('Final clearance completed successfully.');

      setShowSuccess(true);

      await fetchDetails();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to complete clearance';

      setApiError(message);
      setShowError(true);
    } finally {
      setClearanceSubmitting(false);
    }
  };

  const handleAppointedPersonCancel = async (remarks: string) => {
    try {
      setCancelSubmitting(true);

      await appointedPersonCancel(id!, remarks);

      setShowCancelModal(false);

      setSuccessMessage('Work cancelled successfully.');

      setShowSuccess(true);

      await fetchDetails();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to cancel';

      setApiError(message);
      setShowError(true);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleClearance = async () => {
    try {
      setClearanceSubmitting(true);

      await responsiblePersonClearance(id!);

      setItem((prev: any) => ({
        ...prev,
        status: 'RP Cleared',
        'status-details': {
          ...(prev?.['status-details'] || {}),
          code: 'RP_CLEARED',
        },
      }));

      setClearanceDone(true);
      setShowNotifyClearanceButton(true);

      setShowClearanceModal(false);

      setSuccessMessage('Clearance completed successfully.');
      setShowSuccess(true);

      await fetchDetails();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to complete clearance';

      setApiError(message);
      setShowError(true);
    } finally {
      setClearanceSubmitting(false);
    }
  };

  const handleCancelClearance = async (remarks: string) => {
    try {
      setCancelSubmitting(true);

      await responsiblePersonCancel(id!, remarks);
      setShowNotifyClearanceButton(true);

      setShowCancelModal(false);

      setSuccessMessage('Work cancelled successfully.');
      setShowSuccess(true);

      await fetchDetails();
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to cancel';

      setApiError(message);
      setShowError(true);
    } finally {
      setCancelSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!id) return;
    try {
      await deleteHotWorkApproval(Number(id));
      setShowDeleteModal(false);
      setSuccessMessage('Hot work approval successfully deleted.');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        navigate('/hot-work-approval');
      }, 2000);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to delete.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    }
  };

  // Success Toast State
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  if (loading) return <Loader />;
  if (!item) return <div className="p-6 text-red-500">No data found</div>;

  const statusColors = getStatusColors(item.status || '');

  const hideEditDelete = assessmentDone || approvalNotified || approved || clearanceDone || clearanceNotified;

  const handleDownload = async () => {
    try {
      setDownloadSubmitting(true);

      const blob = await getHotWorkApprovalDownload(id!);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `HotWorkApproval_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to download hot work approval.';
      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setDownloadSubmitting(false);
    }
  };

  return (
    <>
      <Header title="Hot Work Approval Details" showBackButton />

      <div className="grid grid-cols-12 min-h-screen">
        <div className="col-span-8 px-6 py-6 overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h2 className="font-semibold text-lg">{item.reference_number || `#HWA${String(item.id || '').padStart(9, '0')}`}</h2>
              <span className={`flex items-center gap-1.5 text-sm font-medium px-2.5 py-0.5 rounded-full ${statusColors.badge}`}>
                <span className={`w-2 h-2 rounded-full ${statusColors.dot}`}></span>
                {item.status || 'Pending'}
              </span>
              {profile?.role === 'appointed_person_hwa' &&
                clearanceApproveIds.includes(currentUserId) &&
                !clearanceDoneAp &&
                !isClearanceEnabled && (
                  <span className="px-3 py-1 text-xs border rounded bg-gray-100 text-orange-500">{remainingTime}</span>
                )}
            </div>

            <div className="flex items-center gap-2">
              {!isTechnician && !hideEditDelete && (
                <>
                  <button onClick={() => setShowDeleteModal(true)} className="p-2 border rounded-md hover:bg-gray-100" title="Delete">
                    <Trash2 size={16} />
                  </button>

                  <button onClick={() => setShowEditModal(true)} className="p-2 border rounded-md hover:bg-gray-100" title="Edit">
                    <Pencil size={16} />
                  </button>
                </>
              )}

              {/* NOTIFY BUTTON */}
              {assessmentDone &&
                !approvalNotified &&
                profile?.role === 'responsible_person_hwa' &&
                item?.created_by?.toString() === currentUserId && (
                  <button
                    onClick={handleNotifyApprovalClick}
                    className="px-5 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 text-sm font-medium ml-2"
                  >
                    Notify for Approval
                  </button>
                )}

              {/* APPROVE BUTTON */}
              {!approved && profile?.role === 'appointed_person_hwa' && approveIds.includes(currentUserId) && (
                <>
                  <button
                    onClick={() => setShowRejectModal(true)}
                    className="px-5 py-2 border border-red-600 text-red-600 rounded-md hover:bg-red-50 text-sm font-medium"
                  >
                    Reject
                  </button>

                  <button
                    onClick={() => setShowApproveModal(true)}
                    className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium"
                  >
                    Approve
                  </button>
                </>
              )}

              {/* RESPONSIBLE PERSON CLEARANCE */}
              {approved &&
                !clearanceDone &&
                profile?.role === 'responsible_person_hwa' &&
                item?.created_by?.toString() === currentUserId && (
                  <>
                    <button onClick={() => setShowCancelModal(true)} className="px-5 py-2 border rounded-md text-white bg-yellow-700">
                      Cancel
                    </button>

                    <button
                      onClick={() => setShowClearanceModal(true)}
                      className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                    >
                      Clearance
                    </button>
                  </>
                )}

              {showNotifyClearanceButton &&
                profile?.role === 'responsible_person_hwa' &&
                item?.created_by?.toString() === currentUserId &&
                !clearanceNotified && (
                  <button onClick={handleNotifyClearanceClick} className="px-5 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">
                    Notify for Clearance
                  </button>
                )}

              {profile?.role === 'appointed_person_hwa' && clearanceApproveIds.includes(currentUserId) && !clearanceDoneAp && (
                <>
                  <button onClick={() => setShowCancelModal(true)} className="px-5 py-2 border rounded-md text-white bg-yellow-700">
                    Cancel
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowClearanceModal(true)}
                      disabled={!isClearanceEnabled}
                      className={`px-5 py-2 text-white rounded-md ${
                        isClearanceEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'
                      }`}
                    >
                      Clearance
                    </button>
                  </div>
                </>
              )}

              {showDownloadButton && profile?.role === 'responsible_person_hwa' && item?.created_by?.toString() === currentUserId && (
                <button
                  onClick={handleDownload}
                  disabled={downloadSubmitting}
                  className="px-5 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2 disabled:opacity-60"
                >
                  <Download size={16} />
                  {downloadSubmitting ? 'Downloading...' : 'Download'}
                </button>
              )}
            </div>
          </div>

          {/* Application Info */}
          <div className="mb-6 grid grid-cols-4 gap-x-6 gap-y-4">
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Hot Work Being Done by</p>
              <p className="font-medium text-sm capitalize">{item.work_by || '-'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Job Number</p>
              <p className="font-medium text-sm">{item.job_number || '-'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Location / Building and Floor</p>
              <p className="font-medium text-sm">{item.location || '-'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Nature of Job</p>
              <p className="font-medium text-sm">{item.nature_of_job || '-'}</p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Permit Expiry Date & Time</p>
              <p className="font-medium text-sm">
                {item.permit_expiry_date
                  ? `${item?.permit_expiry_date ? new Date(item.permit_expiry_date).toLocaleDateString('en-GB') : '-'}, ${item.permit_expiry_time || ''}`
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-gray-400 text-xs mb-0.5">Hot Work Performer</p>
              <p className="font-medium text-sm">{item.people_involved?.hot_work_performer?.name || '-'}</p>
            </div>

            <div>
              <p className="text-gray-400 text-xs mb-0.5">Fire Watch / Safety Monitor</p>
              <p className="font-medium text-sm">{item.people_involved?.fire_watch?.name || '-'}</p>
            </div>
          </div>

          {/* Assessment Card */}
          <div className="mb-6 bg-indigo-50 rounded-xl p-6 flex justify-between items-center">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="40" cy="40" r="38" fill="#E0E7FF" stroke="#C7D2FE" strokeWidth="2" />
                  <circle cx="30" cy="32" r="8" fill="#6366F1" />
                  <circle cx="50" cy="28" r="6" fill="#818CF8" />
                  <rect x="20" y="44" width="40" height="18" rx="4" fill="#4F46E5" />
                  <circle cx="40" cy="44" r="6" fill="#EF4444" />
                  <line x1="40" y1="38" x2="40" y2="50" stroke="white" strokeWidth="2" />
                  <line x1="34" y1="44" x2="46" y2="44" stroke="white" strokeWidth="2" />
                </svg>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-800">Precaution Assessment</h3>

                <p className="text-gray-600 mt-2">
                  You have examined the location and checked the precautions to prevent fire and permission is authorized for this work.
                </p>
              </div>
            </div>

            {!assessmentDone && !isTechnician ? (
              <button
                onClick={() => setShowAssessmentModal(true)}
                className="px-6 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50"
              >
                Assessment
              </button>
            ) : assessmentDone ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowViewDetailsModal(true)}
                  className="px-6 py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50"
                >
                  View Assessment
                </button>

                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={downloadSubmitting}
                  title="Download assessment"
                  aria-label="Download assessment"
                  className="p-2 border border-blue-600 rounded-md hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Download size={18} />
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {/* Activities */}
        <div className="col-span-3 py-6 pr-6 border-l">
          <Activities moduleName="hot_work_approvals" moduleItemId={Number(id)} />
        </div>
      </div>

      {/* ASSESSMENT MODAL */}
      {showAssessmentModal && !isTechnician && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white w-[780px] rounded-xl shadow-xl relative">
            <div className="flex items-center justify-between px-8 py-5 border-b">
              <h2 className="text-xl font-semibold text-gray-800">Precaution Assessment</h2>
              <button onClick={() => setShowAssessmentModal(false)}>
                <X size={22} />
              </button>
            </div>

            <div className="px-8 py-6 space-y-7 max-h-[70vh] overflow-y-auto">
              {ASSESSMENT_SECTIONS.map((section) => (
                <div key={section.title}>
                  <h3 className="font-semibold text-gray-800 mb-3">{section.title}</h3>
                  <div className="space-y-2.5">
                    {section.items.map(({ key, label }) => (
                      <label key={key} className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!checkedItems[key]}
                          onChange={() => toggleCheckItem(key)}
                          className="mt-1 w-4 h-4 accent-red-500"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              <div>
                <h3 className="font-semibold text-gray-800 mb-3">Other Precautions Taken:</h3>
                <textarea
                  value={otherPrecautions}
                  onChange={(e) => setOtherPrecautions(e.target.value)}
                  placeholder="Add description here"
                  className="w-full border rounded-lg px-4 py-3 min-h-[100px]"
                />
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={verifyChecked}
                  onChange={(e) => setVerifyChecked(e.target.checked)}
                  className="mt-1 w-4 h-4 accent-red-500"
                />
                <span className="text-sm text-gray-700">I verify the above location has been examined and precautions checked...</span>
              </label>
            </div>

            <div className="flex justify-end gap-3 px-8 py-5 border-t">
              <button onClick={() => setShowAssessmentModal(false)} className="px-6 py-2 border rounded-md">
                Cancel
              </button>
              <button
                onClick={handleAssessmentNext}
                disabled={assessmentSubmitting || !verifyChecked}
                className="px-6 py-2 bg-red-500 text-white rounded-md disabled:opacity-60"
              >
                {assessmentSubmitting ? 'Submitting...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VIEW DETAILS MODAL */}
      {showViewDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-start justify-center z-50 overflow-y-auto py-8">
          <div className="bg-white w-[780px] rounded-xl shadow-xl relative">
            <div className="flex items-center justify-between px-8 py-5 border-b">
              <h2 className="text-xl font-semibold text-gray-800">Precaution Assessment</h2>
              <button onClick={() => setShowViewDetailsModal(false)} className="text-gray-400 hover:text-gray-700">
                <X size={22} />
              </button>
            </div>

            <div className="px-8 py-6 space-y-7 max-h-[75vh] overflow-y-auto">
              {ASSESSMENT_SECTIONS.map((section) => (
                <div key={section.title}>
                  <h3 className="font-semibold text-gray-800 mb-3">{section.title}</h3>
                  <div className="space-y-2.5">
                    {section.items.map(({ key, label }) => (
                      <div key={key} className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={!!checkedItems[key]}
                          readOnly
                          className="mt-1 w-4 h-4 accent-red-500 pointer-events-none"
                        />
                        <span className="text-sm text-gray-700">{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {otherPrecautions && (
                <div>
                  <h3 className="font-semibold text-gray-800 mb-2">Other Precautions Taken:</h3>
                  <p className="text-sm text-gray-700 border rounded-lg px-4 py-3 bg-gray-50 whitespace-pre-wrap">{otherPrecautions}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end px-8 py-5 border-t">
              <button
                onClick={() => setShowViewDetailsModal(false)}
                className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NOTIFY MODAL */}
      {showNotifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[650px] rounded-xl shadow-xl relative">
            <div className="flex items-center justify-between px-8 py-5 border-b">
              <h2 className="text-xl font-semibold">Notify for Approval</h2>
              <button onClick={() => setShowNotifyModal(false)} className="text-gray-400 hover:text-gray-700">
                <X size={22} />
              </button>
            </div>

            <div className="p-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Appointed Person</label>
              <select
                value={selectedAppointedId || ''}
                onChange={(e) => setSelectedAppointedId(Number(e.target.value))}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Select Appointed Person</option>
                {appointedPersons.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 px-8 py-5 border-t">
              <button onClick={() => setShowNotifyModal(false)} className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={handleNotifyApproval}
                disabled={!selectedAppointedId || notifySubmitting}
                className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-60"
              >
                {notifySubmitting ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NOTIFICATION TOAST */}
      {showNotifyNotification && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-green-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[320px] relative">
            <button onClick={() => setShowNotifyNotification(false)} className="absolute top-3 right-3 text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Notification Sent</h4>
                <p className="text-sm">Notification successfully sent to the Appointed Person to approve.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showClearanceNotification && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-green-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[320px] relative">
            <button onClick={() => setShowClearanceNotification(false)} className="absolute top-3 right-3">
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 mt-0.5" />

              <div>
                <h4 className="font-semibold mb-1">Notification Sent</h4>

                <p className="text-sm">Notification successfully sent to the Appointed Person for clearance.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* APPROVE MODAL */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] rounded-xl shadow-xl p-6 relative">
            <button onClick={() => setShowApproveModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
              <X size={20} />
            </button>
            <h2 className="text-lg font-semibold text-center mb-6">Are you sure you want to approve this Hot Work?</h2>
            <div className="flex justify-center gap-4">
              <button onClick={() => setShowApproveModal(false)} className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={handleApprove}
                disabled={approveSubmitting}
                className="px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-60"
              >
                {approveSubmitting ? 'Approving...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REJECT MODAL */}
      {showRejectModal && (
        <ConfirmationModal
          title="Reject Hot Work Approval"
          message="Are you sure you want to reject this Hot Work?"
          loading={rejectSubmitting}
          onConfirm={handleReject}
          onCancel={() => setShowRejectModal(false)}
        />
      )}

      {/* CLEARANCE MODAL */}
      {showClearanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] rounded-xl shadow-xl p-6 relative">
            <button onClick={() => setShowClearanceModal(false)} className="absolute top-3 right-3">
              <X size={20} />
            </button>

            <h2 className="text-lg font-semibold text-center mb-6">Are you sure you want to mark it as Cleared?</h2>

            <div className="flex justify-center gap-4">
              <button onClick={() => setShowClearanceModal(false)} className="px-6 py-2 border rounded-md">
                Cancel
              </button>

              <button
                onClick={() => {
                  if (profile?.role === 'appointed_person_hwa') {
                    handleAppointedPersonClearance();
                  } else {
                    handleClearance();
                  }
                }}
                disabled={clearanceSubmitting}
                className="px-6 py-2 bg-red-600 text-white rounded-md"
              >
                {clearanceSubmitting ? 'Processing...' : 'Yes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CANCEL MODAL */}
      {showCancelModal && (
        <ConfirmationModal
          title="Cancel Hot Work Approval"
          message="Are you sure you want to cancel this work?"
          loading={cancelSubmitting}
          placeholder="Enter reason for cancellation"
          loadingText="Cancelling..."
          onConfirm={(remarks) => {
            if (profile?.role === 'appointed_person_hwa') {
              handleAppointedPersonCancel(remarks);
            } else {
              handleCancelClearance(remarks);
            }
          }}
          onCancel={() => setShowCancelModal(false)}
        />
      )}

      {showNotifyClearanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[650px] rounded-xl shadow-xl relative">
            <div className="flex items-center justify-between px-8 py-5 border-b">
              <h2 className="text-xl font-semibold">Notify for Clearance</h2>

              <button onClick={() => setShowNotifyClearanceModal(false)}>
                <X size={22} />
              </button>
            </div>

            <div className="p-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Appointed Person</label>

              <select
                value={selectedClearanceAppointedId || ''}
                onChange={(e) => setSelectedClearanceAppointedId(Number(e.target.value))}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Select Appointed Person</option>

                {appointedPersons.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3 px-8 py-5 border-t">
              <button onClick={() => setShowNotifyClearanceModal(false)} className="px-6 py-2 border rounded-md">
                Cancel
              </button>

              <button
                onClick={handleNotifyClearance}
                disabled={!selectedClearanceAppointedId || notifyClearanceSubmitting}
                className="px-6 py-2 bg-red-500 text-white rounded-md"
              >
                {notifyClearanceSubmitting ? 'Sending...' : 'Done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ERROR TOAST */}
      {showError && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] relative">
            <button onClick={() => setShowError(false)} className="absolute top-3 right-3 text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Error</h4>
                <p className="text-sm">{apiError}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SUCCESS TOAST */}
      {showSuccess && (
        <div className="fixed bottom-6 right-6 z-[100]">
          <div className="bg-green-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[320px] relative">
            <button onClick={() => setShowSuccess(false)} className="absolute top-3 right-3 text-white hover:text-gray-200">
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-start gap-3">
              <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold mb-1">Success</h4>
                <p className="text-sm">{successMessage || 'Hot work approval approved successfully.'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showEditModal && !isTechnician && (
        <AddHotWorkApprovalModal
          editId={Number(id)}
          initialData={item}
          editFullData={item}
          onClose={() => setShowEditModal(false)}
          onSuccess={async (message) => {
            setShowEditModal(false);
            await fetchDetails();
            setShowSuccess(true);
            setSuccessMessage(message.message);
            setTimeout(() => setShowSuccess(false), 3000);
          }}
        />
      )}

      {/* DELETE MODAL */}
      {showDeleteModal && !isTechnician && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white w-[420px] rounded-xl shadow-xl p-6 relative">
            <button onClick={() => setShowDeleteModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-gray-800">
              <X size={20} />
            </button>
            <h2 className="text-lg font-semibold text-center mb-6">Are you sure you want to delete this hot work approval?</h2>
            <div className="flex justify-center gap-4">
              <button onClick={() => setShowDeleteModal(false)} className="px-6 py-2 border rounded-md text-gray-600 hover:bg-gray-100">
                Cancel
              </button>
              <button onClick={confirmDelete} className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600">
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HotWorkApprovalDetail;
